import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { LocalDate } from '../../platform/localDate'
import { historyWindow, lowerBound, periodWindow, type CashflowView, type Granularity } from './cashflowPeriod'
import { expectedEcho, scopeQuery, type DetailScope } from './cashflowScope'
import { toCashflow, toReportQuery, type Cashflow, type ReportQuery } from './cashflowWire'

export class CashflowRequestError extends Error {
  readonly status: number
  /** The request field a 422 names, or null. */
  readonly field: string | null
  constructor(status: number, field: string | null = null) {
    super(`Cashflow request failed with ${status}`)
    this.name = 'CashflowRequestError'
    this.status = status
    this.field = field
  }
}

/** The server answered for a different row than the one asked for (a slow response for row A under row B's page). */
export class CashflowScopeMismatch extends Error {
  constructor() {
    super('Cashflow response was scoped to a different row than requested')
    this.name = 'CashflowScopeMismatch'
  }
}

/** The history call names only the bucket series. */
export const HISTORY_INCLUDE = 'buckets'
/** The period call, when entitled, names only the five breakdown sections. */
export const PERIOD_INCLUDE = 'categories,groups,merchants,income_sources,runway'

/**
 * `keepPreviousData` across a period or granularity step, but never across a scope: row A's
 * figures must not stand in for row B's page while B loads. Unscoped keys carry no scope on
 * either side, so the overview keeps its previous data exactly as before.
 */
function keepSameScope(scope: DetailScope | undefined) {
  return <T,>(previous: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined => {
    const key = previousQuery?.queryKey[2]
    const before = typeof key === 'object' && key !== null && 'scope' in key ? key.scope : undefined
    return JSON.stringify(before) === JSON.stringify(scope) ? previous : undefined
  }
}

export const CASHFLOW_ERROR = "Cashflow couldn't be loaded."

/** `fetching` is true while a refetch runs under kept data: the screen sets `aria-busy` from it. */
export type CashflowResult = { state: AsyncState<Cashflow>; retry: () => void; fetching: boolean }

type CashflowParams = {
  granularity: Granularity
  start: LocalDate
  end: LocalDate
  tz: string
  include: string | null
  scope?: DetailScope
}

/**
 * ONE request function for every `/reports/cashflow` reader: one place where a 4xx becomes a
 * `CashflowRequestError` and one place where a wrong-scope echo becomes a `CashflowScopeMismatch`.
 */
async function fetchReport<T extends { window: { scope?: { kind: string; id: string | null } } }>(
  { granularity, start, end, tz, include, scope }: CashflowParams,
  parse: (body: unknown) => T,
): Promise<T> {
  const { data, error, response } = await api.GET('/api/v1/reports/cashflow', {
    params: {
      query: {
        end_date: end,
        start_date: start,
        tz,
        granularity,
        ...(include === null ? {} : { include }),
        ...(scope === undefined ? {} : scopeQuery(scope)),
      },
    },
  })
  if (error !== undefined || data === undefined) throw new CashflowRequestError(response.status)
  const parsed = parse(data)
  // The echo is additive: absent is accepted, present must be the row asked for.
  const echo = parsed.window.scope
  if (scope !== undefined && echo !== undefined) {
    const want = expectedEcho(scope)
    if (echo.kind !== want.kind || echo.id !== want.id) throw new CashflowScopeMismatch()
  }
  return parsed
}

const fetchCashflow = (p: CashflowParams): Promise<Cashflow> => fetchReport(p, toCashflow)

export type CashflowHistoryQuery = {
  granularity: Granularity
  today: LocalDate
  tz: string
  /** `useEntitlement('cashflow_details')`: free never fetches history (M3-R16). */
  enabled: boolean
  /**
   * `available.earliest_transaction_date` from the period call. `undefined` while
   * that call is in flight, `null` when there are no transactions: neither asks.
   */
  earliest: LocalDate | null | undefined
  /** A drill-down: the same window, narrowed to one row. */
  scope?: DetailScope
}

/**
 * The bucket series behind the chart and the table: ten years back, or from the
 * unit holding the earliest transaction when that is later. A LIVE surface
 * (A23): a computed window aggregate with no sync date, so four states and no
 * stale. The money is parsed once, in `toCashflow`, inside the query function —
 * a body that breaks the contract is a query error, never partial data.
 */
export function useCashflowHistory({ granularity, today, tz, enabled, earliest, scope }: CashflowHistoryQuery): CashflowResult {
  const from = typeof earliest === 'string' ? lowerBound(earliest, granularity, today) : null
  const { start: tenYears, end } = historyWindow(granularity, today)
  const start = from ?? tenYears
  const query = useQuery({
    queryKey: keys.cashflow.history({ granularity, start, end, tz, ...(scope === undefined ? {} : { scope }) }),
    queryFn: () => fetchCashflow({ granularity, start, end, tz, include: HISTORY_INCLUDE, scope }),
    enabled: enabled && from !== null,
    placeholderData: keepSameScope(scope),
  })
  const state = fromQuery(query, { message: () => CASHFLOW_ERROR })
  return { state, retry: () => void query.refetch(), fetching: query.isFetching }
}

export type CashflowPeriodQuery = {
  view: CashflowView
  today: LocalDate
  tz: string
  /** `useEntitlement('cashflow_details')`: whether to request the breakdown. */
  details: boolean
  /** A detail page needs only totals and prior, so a scoped call asks for no breakdown. */
  scope?: DetailScope
  /** Default true. A detail page reads the overview's breakdown only for a share it will show. */
  enabled?: boolean
}

/**
 * The selected period: totals, prior and, when entitled, the breakdown. The
 * current unit ends today. `keepPreviousData` keeps the last period on screen
 * while the next loads; `fetching` says so.
 */
export function useCashflowPeriod({ view, today, tz, details, scope, enabled = true }: CashflowPeriodQuery): CashflowResult {
  const { start, end } = periodWindow(view.period, view.granularity, today)
  const { granularity } = view
  const query = useQuery({
    queryKey: keys.cashflow.period({ granularity, start, end, tz, details, ...(scope === undefined ? {} : { scope }) }),
    queryFn: () => fetchCashflow({ granularity, start, end, tz, include: details && scope === undefined ? PERIOD_INCLUDE : null, scope }),
    placeholderData: keepSameScope(scope),
    enabled,
  })
  const state = fromQuery(query, { message: () => CASHFLOW_ERROR })
  return { state, retry: () => void query.refetch(), fetching: query.isFetching }
}

/** Which request field a 422 body names: `{"detail":"tag: ..."}` or `{"detail":[{"loc":["query","limit"]}]}`. */
export function fieldOf(errorBody: unknown): string | null {
  if (typeof errorBody !== 'object' || errorBody === null || !('detail' in errorBody)) return null
  const d = errorBody.detail
  if (typeof d === 'string') {
    const i = d.indexOf(':')
    return i > 0 ? d.slice(0, i) : null
  }
  if (Array.isArray(d)) {
    const loc: unknown = typeof d[0] === 'object' && d[0] !== null && 'loc' in d[0] ? d[0].loc : null
    const f = Array.isArray(loc) ? loc[1] : null
    return typeof f === 'string' ? f : null
  }
  return null
}

export const REPORT_ERROR = "This report couldn't be loaded."
/** Only a 422 that names the window is the user's range (M8-R9); any other 422 is a bug in our request. */
export const REPORT_TOO_LONG = 'This range is too long to report on. Choose a shorter one.'
export const TABLE_LIMIT = 100

/** The server answered a different report than the one asked for. */
export class ReportQueryMismatch extends Error {
  constructor(what: string) {
    super(`Report response did not match the request: ${what}`)
    this.name = 'ReportQueryMismatch'
  }
}

export type ReportQueryRequest = {
  rows: 'category' | 'group' | 'merchant' | 'owner' | 'account' | 'tag'
  columns: 'owner' | null
  granularity: 'month' | 'quarter'
  start: LocalDate
  end: LocalDate
  tz: string
  /** Arrays sorted and de-duplicated by the caller (`reportSpec.queryRequest`). */
  filters: {
    accountIds: string[]
    ownerIds: string[]
    groupIds: string[]
    uncategorised: boolean
    merchantIds: string[]
    noMerchant: boolean
    tags: string[]
    untagged: boolean
    recurring: boolean
  }
}

/** The wire parameters for a report. The table, the chart and the PDF export all build theirs here, so they cannot drift. */
export function reportQueryParams(req: ReportQueryRequest, limit: number) {
  const f = req.filters
  return {
    end_date: req.end,
    start_date: req.start,
    tz: req.tz,
    granularity: req.granularity,
    rows: req.rows,
    ...(req.columns === null ? {} : { columns: req.columns }),
    kind: 'expense' as const,
    limit,
    ...(f.accountIds.length === 0 ? {} : { account_id: f.accountIds }),
    ...(f.ownerIds.length === 0 ? {} : { owner_id: f.ownerIds }),
    ...(f.groupIds.length === 0 ? {} : { group_id: f.groupIds }),
    ...(f.merchantIds.length === 0 ? {} : { merchant_id: f.merchantIds }),
    ...(f.tags.length === 0 ? {} : { tag: f.tags }),
    ...(f.uncategorised ? { uncategorized: true } : {}),
    ...(f.noMerchant ? { no_merchant: true } : {}),
    ...(f.untagged ? { untagged: true } : {}),
    ...(f.recurring ? { recurring: true } : {}),
  }
}

async function fetchReportQuery(req: ReportQueryRequest, limit: number): Promise<ReportQuery> {
  const { data, error, response } = await api.GET('/api/v1/reports/query', {
    params: { query: reportQueryParams(req, limit) },
  })
  if (error !== undefined || data === undefined) throw new CashflowRequestError(response.status, fieldOf(error))
  const q = toReportQuery(data)
  if (q.rowsBy !== req.rows) throw new ReportQueryMismatch('rows_by')
  if (q.kind !== 'expense') throw new ReportQueryMismatch('kind')
  if ((q.columns !== null) !== (req.columns !== null)) throw new ReportQueryMismatch('columns')
  if (!sameFilters(q.filters, req.filters)) throw new ReportQueryMismatch('filters')
  return q
}

const norm = (xs: string[]) => [...new Set(xs.map((x) => x.toLowerCase()))].sort()

/** The request's filters against the server's echo. A filter the server dropped must not read as a filtered report. */
function sameFilters(echo: ReportQuery['filters'], f: ReportQueryRequest['filters']): boolean {
  const same = (a: string[], b: string[]) => JSON.stringify(norm(a)) === JSON.stringify(norm(b))
  return (
    same(echo.accountIds, f.accountIds) &&
    same(echo.ownerIds, f.ownerIds) &&
    same(echo.groupIds, f.groupIds) &&
    same(echo.merchantIds, f.merchantIds) &&
    same(echo.tags, f.tags) &&
    echo.categoryIds.length === 0 &&
    echo.uncategorised === f.uncategorised &&
    echo.noMerchant === f.noMerchant &&
    echo.untagged === f.untagged &&
    echo.recurring === f.recurring
  )
}

/** Keep the previous data across a window step, never across a different rows, columns or filter set. */
function keepSameShape(request: ReportQueryRequest) {
  const shape = (r: Pick<ReportQueryRequest, 'rows' | 'columns' | 'filters'>) =>
    JSON.stringify([r.rows, r.columns, r.filters])
  return <T,>(previous: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined => {
    const key = previousQuery?.queryKey[2]
    if (typeof key !== 'object' || key === null) return undefined
    return shape(key as ReportQueryRequest) === shape(request) ? previous : undefined
  }
}

export type ReportQueryInput = {
  request: ReportQueryRequest
  limit: number
  /** `useEntitlement('cashflow_details')`: free never fetches (M3-R16). */
  enabled: boolean
  isEmpty?: (r: ReportQuery) => boolean
}

/**
 * Every Reports figure, from `GET /reports/query`. A LIVE surface: four states, no stale. The chart type is
 * deliberately not in the key (M8-R2): switching it never refetches.
 */
export function useReportQuery({ request, limit, enabled, isEmpty }: ReportQueryInput): {
  state: AsyncState<ReportQuery>
  retry: () => void
  fetching: boolean
} {
  const query = useQuery({
    queryKey: keys.cashflow.query({ ...request, limit }),
    queryFn: () => fetchReportQuery(request, limit),
    enabled,
    placeholderData: keepSameShape(request),
  })
  const state = fromQuery(query, {
    message: (e) =>
      e instanceof CashflowRequestError && e.status === 422 && (e.field === 'start_date' || e.field === 'end_date')
        ? REPORT_TOO_LONG
        : REPORT_ERROR,
    isEmpty,
  })
  return { state, retry: () => void query.refetch(), fetching: query.isFetching }
}
